// src/Evraklarim/SablonEkle.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  TextField,
  Chip,
  Avatar,
  Stack,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  Skeleton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Badge,
  CircularProgress,
  alpha,
  styled
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useNotify } from 'react-admin';
import {
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  DragIndicator as DragIcon,
  TextFields as TextIcon,
  Create as SignatureIcon,
  DateRange as DateIcon,
  CheckBox as CheckboxIcon,
  RadioButtonChecked as RadioIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetIcon,
  GridOn as GridIcon,
  Mouse as MouseIcon
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// API Base
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.haymestasarim.com/api';

// PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// ==================== RENK PALETİ ====================
const COLORS = {
  primary: '#f57c00',
  secondary: '#2563EB',
  success: '#2e7d32',
  warning: '#ed6c02',
  error: '#d32f2f',
  info: '#0288d1',
  purple: '#9c27b0',
  teal: '#008080',
  background: '#f8fafc',
  surface: '#ffffff',
  text: {
    primary: '#0f172a',
    secondary: '#64748b',
    hint: '#94a3b8'
  },
  border: '#e2e8f0'
};

// ==================== STYLED COMPONENTS ====================
const GradientHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
  color: 'white',
  borderRadius: 24,
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden'
}));

const FieldTypeIcon = ({ type }) => {
  switch(type) {
    case 'text': return <TextIcon sx={{ color: COLORS.primary }} />;
    case 'signature': return <SignatureIcon sx={{ color: COLORS.purple }} />;
    case 'date': return <DateIcon sx={{ color: COLORS.teal }} />;
    case 'checkbox': return <CheckboxIcon sx={{ color: COLORS.success }} />;
    case 'radio': return <RadioIcon sx={{ color: COLORS.warning }} />;
    default: return <TextIcon />;
  }
};

const DraggableField = ({ field, onUpdate, onDelete, pageNum, scale, containerRef }) => {
  const fieldRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: field.x, y: field.y });
  const [size, setSize] = useState({ width: field.width || 200, height: field.height || 40 });

  useEffect(() => {
    setPosition({ x: field.x, y: field.y });
    setSize({ width: field.width || 200, height: field.height || 40 });
  }, [field]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = fieldRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    
    setIsDragging(true);
  };

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  };

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();

    if (isDragging) {
      const newX = (e.clientX - containerRect.left - dragOffset.x) / scale;
      const newY = (e.clientY - containerRect.top - dragOffset.y) / scale;
      
      setPosition({
        x: Math.max(0, Math.min(containerRect.width / scale - size.width, newX)),
        y: Math.max(0, Math.min(containerRect.height / scale - size.height, newY))
      });
    }

    if (isResizing) {
      const newWidth = (e.clientX - containerRect.left) / scale - position.x;
      const newHeight = (e.clientY - containerRect.top) / scale - position.y;
      
      setSize({
        width: Math.max(50, Math.min(containerRect.width / scale - position.x, newWidth)),
        height: Math.max(30, Math.min(containerRect.height / scale - position.y, newHeight))
      });
    }
  }, [isDragging, isResizing, dragOffset, scale, containerRef, position, size]);

  const handleMouseUp = useCallback(() => {
    if (isDragging || isResizing) {
      setIsDragging(false);
      setIsResizing(false);
      onUpdate({
        ...field,
        x: position.x,
        y: position.y,
        width: size.width,
        height: size.height
      });
    }
  }, [isDragging, isResizing, field, position, size, onUpdate]);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = isDragging ? 'grabbing' : 'se-resize';
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
    };
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  if (field.page !== pageNum) return null;

  return (
    <Box
      ref={fieldRef}
      onMouseDown={handleMouseDown}
      sx={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        border: `2px solid ${
          field.type === 'signature' ? COLORS.purple :
          field.type === 'date' ? COLORS.teal :
          field.type === 'checkbox' ? COLORS.success :
          field.type === 'radio' ? COLORS.warning :
          COLORS.primary
        }`,
        backgroundColor: alpha(
          field.type === 'signature' ? COLORS.purple :
          field.type === 'date' ? COLORS.teal :
          field.type === 'checkbox' ? COLORS.success :
          field.type === 'radio' ? COLORS.warning :
          COLORS.primary, 0.1
        ),
        borderRadius: 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: isDragging ? `0 0 15px ${alpha(COLORS.primary, 0.3)}` : 'none',
        zIndex: isDragging ? 9999 : 10,
        '&:hover': {
          backgroundColor: alpha(COLORS.primary, 0.2),
          '& .resize-handle': {
            display: 'block'
          },
          '& .delete-btn': {
            display: 'flex'
          }
        }
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <FieldTypeIcon type={field.type} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {field.label || field.type}
        </Typography>
      </Stack>
      
      {/* Boyutlandırma tutamacı */}
      <Box
        className="resize-handle"
        onMouseDown={handleResizeMouseDown}
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: 16,
          height: 16,
          cursor: 'se-resize',
          backgroundColor: COLORS.primary,
          borderRadius: '0 0 4px 0',
          display: 'none',
          '&:hover': {
            backgroundColor: COLORS.secondary,
          }
        }}
      />
      
      {/* Sil butonu */}
      <IconButton
        size="small"
        className="delete-btn"
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          display: 'none',
          bgcolor: COLORS.error,
          color: 'white',
          padding: '4px',
          '&:hover': { bgcolor: '#b71c1c' },
          zIndex: 20
        }}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(field.id);
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
      
      {/* Pozisyon göstergesi */}
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: -25,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: 'white',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: '11px',
            whiteSpace: 'nowrap',
            zIndex: 10000
          }}
        >
          X: {Math.round(position.x)}px, Y: {Math.round(position.y)}px
        </Box>
      )}
    </Box>
  );
};

// ==================== ANA BİLEŞEN ====================
export default function SablonEkle() {
  const navigate = useNavigate();
  const notify = useNotify();
  
  // Dil
  const [lang, setLang] = useState('tr');
  
  // State'ler
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [fileName, setFileName] = useState('');
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [fields, setFields] = useState([]);
  const [selectedFieldType, setSelectedFieldType] = useState('text');
  const [isAddingField, setIsAddingField] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Dil çevirileri
  const t = {
    tr: {
      title: 'Şablon Ekle',
      subtitle: 'PDF veya Word dosyası yükleyin, alanları işaretleyin',
      steps: ['Dosya Yükle', 'Alanları İşaretle', 'Bilgileri Gir', 'Kaydet'],
      upload: {
        title: 'Dosya Yükle',
        dragDrop: 'Sürükleyin veya tıklayın',
        formats: 'PDF, DOCX, DOC (max 20MB)',
        selected: 'Seçilen Dosya',
        change: 'Değiştir',
        next: 'İleri'
      },
      fields: {
        title: 'Alanları İşaretle',
        types: {
          text: 'Metin',
          signature: 'İmza',
          date: 'Tarih',
          checkbox: 'Seçenek',
          radio: 'Çoklu Seçim'
        },
        addField: 'Alan Ekle',
        instructions: 'PDF üzerinde tıklayarak alan ekleyin, sürükleyerek taşıyın',
        fieldsList: 'Alanlar Listesi',
        noFields: 'Henüz alan eklenmemiş',
        page: 'Sayfa',
        zoomIn: 'Yakınlaştır',
        zoomOut: 'Uzaklaştır',
        reset: 'Sıfırla',
        previous: 'Önceki',
        next: 'İleri'
      },
      info: {
        title: 'Şablon Bilgileri',
        name: 'Şablon Adı',
        namePlaceholder: 'Örn: Vekaletname Şablonu',
        description: 'Açıklama',
        descriptionPlaceholder: 'Şablon hakkında açıklama',
        category: 'Kategori',
        categoryPlaceholder: 'Kategori seçin',
        tags: 'Etiketler',
        tagsPlaceholder: 'Etiket ekle (Enter)',
        categories: {
          contract: 'Sözleşme',
          powerOfAttorney: 'Vekaletname',
          form: 'Form',
          invoice: 'Fatura',
          other: 'Diğer'
        },
        back: 'Geri',
        save: 'Kaydet'
      },
      success: 'Şablon başarıyla kaydedildi',
      error: 'Bir hata oluştu',
      confirmClose: 'Değişiklikler kaydedilmeyecek. Emin misiniz?'
    },
    en: {
      title: 'Add Template',
      subtitle: 'Upload PDF or Word file, mark fields',
      steps: ['Upload File', 'Mark Fields', 'Enter Info', 'Save'],
      upload: {
        title: 'Upload File',
        dragDrop: 'Drag & drop or click',
        formats: 'PDF, DOCX, DOC (max 20MB)',
        selected: 'Selected File',
        change: 'Change',
        next: 'Next'
      },
      fields: {
        title: 'Mark Fields',
        types: {
          text: 'Text',
          signature: 'Signature',
          date: 'Date',
          checkbox: 'Checkbox',
          radio: 'Radio'
        },
        addField: 'Add Field',
        instructions: 'Click on PDF to add field, drag to move',
        fieldsList: 'Fields List',
        noFields: 'No fields added yet',
        page: 'Page',
        zoomIn: 'Zoom In',
        zoomOut: 'Zoom Out',
        reset: 'Reset',
        previous: 'Previous',
        next: 'Next'
      },
      info: {
        title: 'Template Information',
        name: 'Template Name',
        namePlaceholder: 'e.g., Power of Attorney Template',
        description: 'Description',
        descriptionPlaceholder: 'Description about template',
        category: 'Category',
        categoryPlaceholder: 'Select category',
        tags: 'Tags',
        tagsPlaceholder: 'Add tag (Enter)',
        categories: {
          contract: 'Contract',
          powerOfAttorney: 'Power of Attorney',
          form: 'Form',
          invoice: 'Invoice',
          other: 'Other'
        },
        back: 'Back',
        save: 'Save'
      },
      success: 'Template saved successfully',
      error: 'An error occurred',
      confirmClose: 'Changes will not be saved. Are you sure?'
    },
    de: {
      title: 'Vorlage Hinzufügen',
      subtitle: 'PDF oder Word-Datei hochladen, Felder markieren',
      steps: ['Datei Hochladen', 'Felder Markieren', 'Informationen', 'Speichern'],
      upload: {
        title: 'Datei Hochladen',
        dragDrop: 'Ziehen oder klicken',
        formats: 'PDF, DOCX, DOC (max 20MB)',
        selected: 'Ausgewählte Datei',
        change: 'Ändern',
        next: 'Weiter'
      },
      fields: {
        title: 'Felder Markieren',
        types: {
          text: 'Text',
          signature: 'Unterschrift',
          date: 'Datum',
          checkbox: 'Checkbox',
          radio: 'Radio'
        },
        addField: 'Feld Hinzufügen',
        instructions: 'Auf PDF klicken um Feld hinzuzufügen, ziehen zum Verschieben',
        fieldsList: 'Felder Liste',
        noFields: 'Keine Felder hinzugefügt',
        page: 'Seite',
        zoomIn: 'Vergrößern',
        zoomOut: 'Verkleinern',
        reset: 'Zurücksetzen',
        previous: 'Zurück',
        next: 'Weiter'
      },
      info: {
        title: 'Vorlageninformationen',
        name: 'Vorlagenname',
        namePlaceholder: 'z.B. Vollmacht Vorlage',
        description: 'Beschreibung',
        descriptionPlaceholder: 'Beschreibung der Vorlage',
        category: 'Kategorie',
        categoryPlaceholder: 'Kategorie auswählen',
        tags: 'Tags',
        tagsPlaceholder: 'Tag hinzufügen (Enter)',
        categories: {
          contract: 'Vertrag',
          powerOfAttorney: 'Vollmacht',
          form: 'Formular',
          invoice: 'Rechnung',
          other: 'Andere'
        },
        back: 'Zurück',
        save: 'Speichern'
      },
      success: 'Vorlage erfolgreich gespeichert',
      error: 'Ein Fehler ist aufgetreten',
      confirmClose: 'Änderungen werden nicht gespeichert. Sind Sie sicher?'
    }
  }[lang];

  // Dosya yükleme
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      notify('Dosya boyutu 20MB\'dan büyük olamaz', { type: 'error' });
      return;
    }

    const type = file.type;
    if (type === 'application/pdf') {
      setFileType('pdf');
    } else if (type === 'application/msword' || type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      setFileType('word');
    } else {
      notify('Lütfen PDF veya Word dosyası seçin', { type: 'error' });
      return;
    }

    setFile(file);
    setFileName(file.name);
    
    // PDF için preview URL oluştur
    if (type === 'application/pdf') {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }

    notify('Dosya yüklendi', { type: 'success' });
  };

  // PDF yükleme başarılı
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Alan ekle
  const handleAddField = (e) => {
    if (!isAddingField || !containerRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const x = e.clientX - container.left;
    const y = e.clientY - container.top;

    const newField = {
      id: Date.now(),
      type: selectedFieldType,
      page: pageNumber,
      x: x / scale,
      y: y / scale,
      width: selectedFieldType === 'signature' ? 200 : 150,
      height: selectedFieldType === 'signature' ? 60 : 40,
      label: `${t.fields.types[selectedFieldType]} ${fields.length + 1}`,
      required: true
    };

    setFields([...fields, newField]);
    setIsAddingField(false);
  };

  // Alan güncelle
  const handleFieldUpdate = (updatedField) => {
    setFields(fields.map(f => f.id === updatedField.id ? updatedField : f));
  };

  // Alan sil
  const handleFieldDelete = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
  };

  // Etiket ekle
  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  // Etiket sil
  const handleDeleteTag = (tagToDelete) => {
    setTags(tags.filter(t => t !== tagToDelete));
  };

  // Şablon kaydet
  const handleSave = async () => {
    if (!templateName) {
      notify('Şablon adı gerekli', { type: 'warning' });
      return;
    }

    if (fields.length === 0) {
      notify('En az bir alan eklemelisiniz', { type: 'warning' });
      return;
    }

    setLoading(true);

    try {
      // FormData oluştur
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', templateName);
      formData.append('description', templateDesc);
      formData.append('category', category);
      formData.append('tags', JSON.stringify(tags));
      formData.append('fields', JSON.stringify(fields));
      formData.append('totalPages', numPages);

      // API'ye gönder
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/templates`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (res.ok) {
        notify(t.success, { type: 'success' });
        navigate('/evraklarim');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Template save error:', error);
      notify(t.error, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Sayfayı terk etme kontrolü
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (file || fields.length > 0 || templateName) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [file, fields, templateName]);

  // Preview URL temizleme
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const steps = t.steps;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: COLORS.background, pb: 6 }}>
      {/* Header */}
      <GradientHeader elevation={0}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" fontWeight={900} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudUploadIcon sx={{ fontSize: 32 }} />
                {t.title}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                {t.subtitle}
              </Typography>
            </Box>
            <IconButton 
              onClick={() => {
                if (file || fields.length > 0 || templateName) {
                  if (window.confirm(t.confirmClose)) {
                    navigate('/evraklarim');
                  }
                } else {
                  navigate('/evraklarim');
                }
              }}
              sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Container>
      </GradientHeader>

      <Container maxWidth="xl" sx={{ mt: -2 }}>
        {/* Stepper */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* İçerik */}
        <Paper sx={{ p: 4, borderRadius: 4 }}>
          {/* Adım 1: Dosya Yükle */}
          {activeStep === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                />
                
                {!file ? (
                  <Box
                    onClick={() => fileInputRef.current.click()}
                    sx={{
                      border: `2px dashed ${COLORS.primary}`,
                      borderRadius: 4,
                      p: 8,
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': {
                        borderColor: COLORS.secondary,
                        bgcolor: alpha(COLORS.primary, 0.02)
                      }
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 80, color: COLORS.primary, mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      {t.upload.dragDrop}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.upload.formats}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Paper
                      sx={{
                        p: 4,
                        bgcolor: alpha(COLORS.success, 0.05),
                        border: `1px solid ${COLORS.success}`,
                        borderRadius: 4
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={3}>
                        {fileType === 'pdf' ? (
                          <PdfIcon sx={{ fontSize: 48, color: COLORS.error }} />
                        ) : (
                          <WordIcon sx={{ fontSize: 48, color: COLORS.primary }} />
                        )}
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                          <Typography variant="h6">{fileName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {(file.size / 1024 / 1024).toFixed(2)} MB • {fileType.toUpperCase()}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          onClick={() => fileInputRef.current.click()}
                          startIcon={<CloudUploadIcon />}
                        >
                          {t.upload.change}
                        </Button>
                      </Stack>
                    </Paper>
                  </Box>
                )}

                <Box sx={{ mt: 4 }}>
                  <Button
                    variant="contained"
                    disabled={!file}
                    onClick={() => setActiveStep(1)}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      bgcolor: COLORS.primary,
                      '&:hover': { bgcolor: COLORS.secondary },
                      px: 4,
                      py: 1.5
                    }}
                  >
                    {t.upload.next}
                  </Button>
                </Box>
              </Box>
            </motion.div>
          )}

          {/* Adım 2: Alanları İşaretle */}
          {activeStep === 1 && fileType === 'pdf' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Grid container spacing={3}>
                {/* PDF Görüntüleyici */}
                <Grid item xs={12} md={8}>
                  <Paper
                    variant="outlined"
                    ref={containerRef}
                    onClick={handleAddField}
                    sx={{
                      height: '70vh',
                      position: 'relative',
                      overflow: 'auto',
                      cursor: isAddingField ? 'crosshair' : 'default',
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {previewUrl && (
                      <Document
                        file={previewUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        loading={
                          <Box textAlign="center">
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
                          </Box>
                        }
                      >
                        <Page
                          pageNumber={pageNumber}
                          scale={scale}
                          renderTextLayer={true}
                          renderAnnotationLayer={true}
                        />
                      </Document>
                    )}

                    {/* Alanlar */}
                    {fields.map((field) => (
                      <DraggableField
                        key={field.id}
                        field={field}
                        onUpdate={handleFieldUpdate}
                        onDelete={handleFieldDelete}
                        pageNum={pageNumber}
                        scale={scale}
                        containerRef={containerRef}
                      />
                    ))}

                    {/* Alan ekleme modu göstergesi */}
                    {isAddingField && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: COLORS.primary,
                          color: 'white',
                          px: 3,
                          py: 1,
                          borderRadius: 4,
                          zIndex: 100,
                          boxShadow: 3
                        }}
                      >
                        <Typography variant="body2">
                          {t.fields.types[selectedFieldType]} eklemek için tıklayın
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  {/* Sayfa kontrolleri */}
                  <Stack direction="row" spacing={2} sx={{ mt: 2 }} justifyContent="center">
                    <Button
                      variant="outlined"
                      onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                      disabled={pageNumber <= 1}
                      startIcon={<ArrowBackIcon />}
                    >
                      {t.fields.previous}
                    </Button>
                    <Typography sx={{ alignSelf: 'center' }}>
                      {t.fields.page} {pageNumber} / {numPages || '?'}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setPageNumber(p => Math.min(numPages || p, p + 1))}
                      disabled={pageNumber >= (numPages || 1)}
                      endIcon={<ArrowForwardIcon />}
                    >
                      {t.fields.next}
                    </Button>
                    <IconButton onClick={() => setScale(s => Math.max(0.5, s - 0.1))}>
                      <ZoomOutIcon />
                    </IconButton>
                    <IconButton onClick={() => setScale(s => Math.min(2, s + 0.1))}>
                      <ZoomInIcon />
                    </IconButton>
                    <IconButton onClick={() => setScale(1)}>
                      <ResetIcon />
                    </IconButton>
                  </Stack>
                </Grid>

                {/* Alan kontrolleri */}
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: COLORS.primary }}>
                      {t.fields.fieldsList}
                    </Typography>
                    
                    {/* Alan tipi seçici */}
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>{t.fields.addField}</InputLabel>
                      <Select
                        value={selectedFieldType}
                        onChange={(e) => setSelectedFieldType(e.target.value)}
                        label={t.fields.addField}
                      >
                        {Object.entries(t.fields.types).map(([key, value]) => (
                          <MenuItem key={key} value={key}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <FieldTypeIcon type={key} />
                              <span>{value}</span>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => setIsAddingField(!isAddingField)}
                      color={isAddingField ? 'success' : 'primary'}
                      startIcon={<AddIcon />}
                      sx={{ mb: 3 }}
                    >
                      {isAddingField ? 'İptal' : t.fields.addField}
                    </Button>

                    <Divider sx={{ my: 2 }} />

                    {/* Alan listesi */}
                    {fields.length > 0 ? (
                      <List>
                        {fields.map((field) => (
                          <ListItem
                            key={field.id}
                            sx={{
                              border: `1px solid ${COLORS.border}`,
                              borderRadius: 2,
                              mb: 1,
                              bgcolor: alpha(
                                field.type === 'signature' ? COLORS.purple :
                                field.type === 'date' ? COLORS.teal :
                                field.type === 'checkbox' ? COLORS.success :
                                field.type === 'radio' ? COLORS.warning :
                                COLORS.primary, 0.02
                              )
                            }}
                          >
                            <ListItemIcon>
                              <DragIcon sx={{ color: COLORS.text.hint, cursor: 'grab' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={field.label}
                              secondary={`Sayfa ${field.page} • X:${Math.round(field.x)} Y:${Math.round(field.y)}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton edge="end" size="small" onClick={() => handleFieldDelete(field.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Alert severity="info">
                        {t.fields.noFields}
                      </Alert>
                    )}

                    <Box sx={{ mt: 3 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => setActiveStep(2)}
                        disabled={fields.length === 0}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          bgcolor: COLORS.primary,
                          '&:hover': { bgcolor: COLORS.secondary }
                        }}
                      >
                        {t.fields.next}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </motion.div>
          )}

          {/* Adım 3: Bilgiler */}
          {activeStep === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label={t.info.name}
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder={t.info.namePlaceholder}
                      variant="outlined"
                      required
                    />

                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label={t.info.description}
                      value={templateDesc}
                      onChange={(e) => setTemplateDesc(e.target.value)}
                      placeholder={t.info.descriptionPlaceholder}
                      variant="outlined"
                    />

                    <FormControl fullWidth>
                      <InputLabel>{t.info.category}</InputLabel>
                      <Select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        label={t.info.category}
                      >
                        {Object.entries(t.info.categories).map(([key, value]) => (
                          <MenuItem key={key} value={key}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Box>
                      <TextField
                        fullWidth
                        label={t.info.tags}
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleAddTag}
                        placeholder={t.info.tagsPlaceholder}
                        variant="outlined"
                      />
                      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                        {tags.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            onDelete={() => handleDeleteTag(tag)}
                            sx={{
                              bgcolor: alpha(COLORS.primary, 0.1),
                              color: COLORS.primary
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 3, bgcolor: alpha(COLORS.info, 0.02) }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                      Özet
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Dosya
                        </Typography>
                        <Typography variant="body2">
                          {fileName}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Sayfa Sayısı
                        </Typography>
                        <Typography variant="body2">
                          {numPages || '-'}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Alan Sayısı
                        </Typography>
                        <Typography variant="body2">
                          {fields.length}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Alan Tipleri
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                          {[...new Set(fields.map(f => f.type))].map(type => (
                            <Chip
                              key={type}
                              size="small"
                              icon={<FieldTypeIcon type={type} />}
                              label={t.fields.types[type]}
                              sx={{
                                bgcolor: alpha(
                                  type === 'signature' ? COLORS.purple :
                                  type === 'date' ? COLORS.teal :
                                  type === 'checkbox' ? COLORS.success :
                                  type === 'radio' ? COLORS.warning :
                                  COLORS.primary, 0.1
                                )
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="outlined"
                      onClick={() => setActiveStep(1)}
                      startIcon={<ArrowBackIcon />}
                      sx={{ px: 4, py: 1.5 }}
                    >
                      {t.info.back}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setActiveStep(3)}
                      disabled={!templateName}
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        bgcolor: COLORS.primary,
                        '&:hover': { bgcolor: COLORS.secondary },
                        px: 4,
                        py: 1.5
                      }}
                    >
                      {t.info.save}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </motion.div>
          )}

          {/* Adım 4: Kaydet ve Onayla */}
          {activeStep === 3 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckIcon sx={{ fontSize: 80, color: COLORS.success, mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Şablon Kaydedilecek
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Lütfen bilgileri kontrol edin
                </Typography>

                <Paper
                  sx={{
                    p: 4,
                    maxWidth: 500,
                    mx: 'auto',
                    bgcolor: alpha(COLORS.primary, 0.02),
                    borderRadius: 4
                  }}
                >
                  <Stack spacing={2} sx={{ textAlign: 'left' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Şablon Adı
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {templateName}
                      </Typography>
                    </Box>
                    
                    {templateDesc && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Açıklama
                        </Typography>
                        <Typography variant="body2">
                          {templateDesc}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Kategori
                      </Typography>
                      <Typography variant="body2">
                        {t.info.categories[category] || category}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Dosya
                      </Typography>
                      <Typography variant="body2">
                        {fileName}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Toplam Alan
                      </Typography>
                      <Typography variant="body2">
                        {fields.length} alan
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 4 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setActiveStep(2)}
                    startIcon={<ArrowBackIcon />}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    Geri
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    sx={{
                      bgcolor: COLORS.success,
                      '&:hover': { bgcolor: '#1e5f2e' },
                      px: 4,
                      py: 1.5
                    }}
                  >
                    {loading ? 'Kaydediliyor...' : 'Kaydet'}
                  </Button>
                </Stack>
              </Box>
            </motion.div>
          )}
        </Paper>
      </Container>
    </Box>
  );
}